import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import SublocationMachinesForm from '../AddCollection/SublocationMachinesForm';
import { FormikProvider, useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import {
  setTotalTokenCollected,
  setTotalTicketDispensed,
  setTotalPrizeDispensed,
  setTransformedSublocations,
  setSubTotalRevenues,
  setTotalTokenChangerRevenueValue,
  setRevenueMeterData,
  setTotalTokenDispensed,
  setFormikData,
} from '../../../redux-slice/reconciliationCollection';
import {
  getFieldName,
  getInitialIntercardState,
  getRevenueInLocalCurrency,
  getSubTotalsPerAsset,
  getTotalPrizeDispensed,
  getTotalTicketDispensed,
  getTotalTokenCollected,
  getTotalTokenDispensed,
  getTotalTokenRevenueCollected,
  getTransformedData,
} from '../../../utils/collection-methods';
import { DEFAULT_EXCHANGE_RATE } from '../../../constants/CurrencyConstants';
import { NoContent } from '../../../components/shared';
import SublocationNavigator from '../AddCollection/SublocationNavigator';
import { checkObjectNotEmpty, checkValueNotNullUndefinedBlank } from '../../../utils/common-methods';
import { FIELD_NAME, METER_TYPE } from '../../../constants/MeterTypeFieldMap';
import { getAssetHasReconciliationVarianceWarning } from '../../../utils/reconciliation-childField-methods';

const assetFieldBelongsToIds = (fieldKey = '', sublocationId, assetId) =>
  String(fieldKey).includes(`_${sublocationId}_${assetId}`);

const isVarianceWarningField = (fieldKey = '') =>
  (fieldKey.includes(FIELD_NAME.BILLS_COLLECTED) && fieldKey.includes(METER_TYPE.CASH)) ||
  (fieldKey.includes(FIELD_NAME.COINS_COLLECTED) &&
    (fieldKey.includes(METER_TYPE.CASH) || fieldKey.includes(METER_TYPE.COIN))) ||
  (fieldKey.includes(FIELD_NAME.METER_ADJUST) && fieldKey.includes(METER_TYPE.CASH)) ||
  ((fieldKey.includes(FIELD_NAME.EXPECTED_REVENUE) || fieldKey.includes(FIELD_NAME.SEEDLIVE_REVENUE)) &&
    fieldKey.includes(METER_TYPE.CREDIT)) ||
  (fieldKey.includes(FIELD_NAME.REVENUE_COLLECTED_FROM_TOKEN_CHANGER) && fieldKey.includes(METER_TYPE.TOKEN_CHANGER));

const displayName = 'ReconciliationCollectionForm';
const ReconciliationCollectionForm = React.forwardRef(
  (
    {
      sublocations,
      allSublocations,
      initialStateAndScheme,
      onConfirm,
      isReconciliation = false,
      isVerified = false,
      readFromDraft,
      transactionId,
      turnOffDraft,
      searchState,
      sortState = false,
      onDeleteSubLocationAssets,
      handleSublocationDeleteClick,
      saveAsDraftHandler,
      showOnlyAssetsWithErrors = false,
      onSublocationAssetsUpdate,
    },
    ref,
  ) => {
    const { scheme } = initialStateAndScheme;
    const [initialStates, setInitialStates] = useState({});
    const [sublocationWarnings, setSublocationWarnings] = useState({});
    const [assetWarningsBySublocation, setAssetWarningsBySublocation] = useState({});
    const { reRender } = useSelector((state) => state.reconciliationCollection);
    const dispatch = useDispatch();
    const isInitialLoad = useRef(true);
    const prevIntercardDataRef = useRef(null);

    const handleSublocationWarningChange = useCallback((sublocationId, hasWarning, assetWarnings = {}) => {
      setSublocationWarnings((prev) => {
        if (prev?.[sublocationId] === hasWarning) {
          return prev;
        }

        return { ...prev, [sublocationId]: hasWarning };
      });

      setAssetWarningsBySublocation((prev) => {
        const previousAssetWarnings = prev?.[sublocationId] || {};
        const nextAssetWarnings = assetWarnings || {};
        const hasSameWarnings =
          Object.keys(previousAssetWarnings).length === Object.keys(nextAssetWarnings).length &&
          Object.keys(nextAssetWarnings).every(
            (assetId) => previousAssetWarnings[assetId] === nextAssetWarnings[assetId],
          );

        if (hasSameWarnings) {
          return prev;
        }

        return { ...prev, [sublocationId]: nextAssetWarnings };
      });
    }, []);

    useEffect(() => {
      setInitialStates(initialStateAndScheme.initialState);
    }, [initialStateAndScheme]);
    const {
      averageTokenValueInUSD,
      revenueMeterData,
      reconciliationWarningForModal,
      sublocationDatesForReconciliation,
      updateStatesForReconciliationSublocationDatesChange,
      intercardDataForReconciliation,
      locationCurrency,
    } = useSelector((state) => state.reconciliationCollection);

    const { playCards } = useSelector((state) => state.playCard);

    // Compute intercard updates for IMPULSE_READER fields
    const intercardUpdates = useMemo(() => {
      // Skip on initial load - only compute updates after dateOfReading changes
      if (isInitialLoad.current) {
        return {};
      }

      if (!intercardDataForReconciliation || Object.keys(intercardDataForReconciliation).length === 0) {
        return {};
      }

      let updateStates = {};

      // Part 1: Get initial intercard states for all assets
      const currentAssets = allSublocations?.flatMap((sub) => sub.assets) || [];
      currentAssets.forEach((asset) => {
        if (
          intercardDataForReconciliation[asset?.id] &&
          intercardDataForReconciliation[asset?.id]?.intercardData?.length > 0
        ) {
          const intercardInitialStates = getInitialIntercardState(
            intercardDataForReconciliation[asset?.id]?.intercardData,
            asset,
          );
          updateStates = { ...updateStates, ...intercardInitialStates };
        }
      });

      // Part 2: Update IMPULSE_READER fields with creditCard values
      if (Array.isArray(sublocations) && sublocations.length > 0) {
        sublocations.forEach((sublocation) => {
          const sublocationId = sublocation?.id;
          sublocation?.assets?.forEach((asset) => {
            const impulseReaderMeters = asset?.assetMeters?.filter(
              (assetMeter) => assetMeter?.meter?.name === METER_TYPE.IMPULSE_READER,
            );

            if (!impulseReaderMeters || impulseReaderMeters.length === 0) {
              return;
            }

            const intercardEntry = intercardDataForReconciliation?.[asset?.id];
            if (!intercardEntry || !Array.isArray(intercardEntry?.intercardData)) {
              return;
            }

            const creditCardValue = Number(intercardEntry?.intercardData?.[0]?.creditCard ?? 0);

            impulseReaderMeters.forEach((assetMeter, index) => {
              const fieldName = getFieldName(
                METER_TYPE.IMPULSE_READER,
                FIELD_NAME.GAME_REVENUE,
                sublocationId,
                asset?.id,
                assetMeter?.meterId,
                assetMeter?.id,
              );

              if (fieldName) {
                const desiredValue = index === 0 ? creditCardValue : 0;
                updateStates[fieldName] = desiredValue;
              }
            });
          });
        });
      }

      return updateStates;
    }, [intercardDataForReconciliation, sublocations, allSublocations]);

    // Track when intercardDataForReconciliation changes after initial load
    useEffect(() => {
      if (isInitialLoad.current && intercardDataForReconciliation) {
        // Store the initial intercard data reference
        prevIntercardDataRef.current = intercardDataForReconciliation;
        isInitialLoad.current = false;
      }
    }, [intercardDataForReconciliation]);

    // Merge initialStates with intercardUpdates for formik's initialValues
    const mergedInitialValues = useMemo(
      () => ({ ...initialStates, ...intercardUpdates }),
      [initialStates, intercardUpdates],
    );

    const formik = useFormik({
      initialValues: mergedInitialValues,
      validationSchema: scheme,
      enableReinitialize: true,
      onSubmit: () => {
        onConfirm();
      },
    });

    // Apply intercard updates after dateOfReading changes (not on initial load)
    useEffect(() => {
      if (Object.keys(intercardUpdates).length > 0) {
        formik.setValues((prevValues) => ({ ...prevValues, ...intercardUpdates }));
      }
    }, [intercardUpdates]);

    useEffect(() => {
      ref.current = formik;
      if (formik.values && Object.values(formik.values).length > 0) {
        let totalTicketDispensed = 0;
        let totalPrizeDispensed = 0;
        let totalTokenCollected = 0;
        let totalTokenChangerRevenueCollected = 0;
        let totalTokenDispensed = 0;
        const exchangeRate = locationCurrency?.exchangeRate
          ? Number(locationCurrency?.exchangeRate)
          : DEFAULT_EXCHANGE_RATE;
        Object.entries(formik.values).map(([key, value]) => {
          totalTicketDispensed = getTotalTicketDispensed(totalTicketDispensed, key, value);
          totalPrizeDispensed = getTotalPrizeDispensed(totalPrizeDispensed, key, value);
          totalTokenCollected = getTotalTokenCollected(totalTokenCollected, key, value);
          totalTokenDispensed = getTotalTokenDispensed(totalTokenDispensed, key, value);
          totalTokenChangerRevenueCollected = getTotalTokenRevenueCollected(
            totalTokenChangerRevenueCollected,
            key,
            formik.values,
            exchangeRate,
          );
        });
        if (Object.values(formik.values).some((el) => el !== '')) {
          const { transformedSublocations, revenueMeterData, formikData } = getTransformedData(
            formik.values,
            allSublocations,
            true,
          );
          dispatch(setRevenueMeterData(revenueMeterData));
          dispatch(setTransformedSublocations(transformedSublocations));
          dispatch(setFormikData(formikData));
        }
        dispatch(setTotalTokenCollected(totalTokenCollected));
        dispatch(setTotalTokenDispensed(totalTokenDispensed));
        dispatch(setTotalTicketDispensed(totalTicketDispensed));
        dispatch(setTotalPrizeDispensed(totalPrizeDispensed));
        dispatch(setTotalTokenChangerRevenueValue(totalTokenChangerRevenueCollected));
      }
    }, [formik.values, reRender]);

    useEffect(() => {
      if (
        checkValueNotNullUndefinedBlank(updateStatesForReconciliationSublocationDatesChange) &&
        checkObjectNotEmpty(updateStatesForReconciliationSublocationDatesChange)
      ) {
        formik.setValues({ ...formik.values, ...updateStatesForReconciliationSublocationDatesChange });
      }
    }, [sublocationDatesForReconciliation]);

    useEffect(() => {
      const rawResults = revenueMeterData
        ?.map((data) => {
          // Look up the sublocation to determine whether it is Standalone.
          // Standalone (calculateRent = true): feed/credit revenue is included in totals.
          // Non-Standalone: feed/credit revenue is excluded from totals and journal entry.
          const sublocationForAsset = allSublocations?.find((sub) => String(sub.id) === String(data?.sublocationId));
          const isStandaloneForAsset = sublocationForAsset?.calculateRent ?? false;
          const res = getSubTotalsPerAsset(
            data?.assetId,
            data?.data,
            locationCurrency?.exchangeRate,
            data?.productCategories,
            isStandaloneForAsset,
            sublocationForAsset?.creditCashControl,
          );
          if (Object.values(res).length === 0) {
            return false;
          }
          res.sublocationId = data?.sublocationId;
          res.assetId = data?.assetId;
          return res;
        })
        .filter((el) => el !== false);
      let total = 0,
        totalInUSD = 0;
      for (const key in playCards) {
        // For Non-Standalone sublocations, exclude credit-card playcard sales from the
        // submitted/approved total; only manual cash playcards are counted.
        const sublocationForPlaycard = allSublocations?.find((sub) => String(sub.id) === String(key));
        const isStandaloneForPlaycard = sublocationForPlaycard?.calculateRent ?? false;
        const adjustedPlaycardSales = isStandaloneForPlaycard
          ? Number(playCards[key]?.adjustedPlaycardSales) || 0
          : (Number(playCards[key]?.adjustedPlaycardSales) || 0) - (Number(playCards[key]?.netCreditCardTotal) || 0);
        const adjustedPlaycardSalesInLocalCurrency = adjustedPlaycardSales
          ? getRevenueInLocalCurrency(adjustedPlaycardSales, locationCurrency?.exchangeRate)
          : 0;

        total += adjustedPlaycardSalesInLocalCurrency;
        totalInUSD += adjustedPlaycardSales;
      }
      rawResults['total'] = total;
      rawResults['totalInUSD'] = totalInUSD;
      dispatch(setSubTotalRevenues(rawResults));
    }, [revenueMeterData, averageTokenValueInUSD, playCards, reRender, JSON.stringify(locationCurrency)]);
    const handleDelete = (row) => {
      if (onDeleteSubLocationAssets) {
        onDeleteSubLocationAssets(row.id); // Pass sublocation ID or any other identifier
      }
    };

    useEffect(() => {
      if (formik?.errors && Object.values(formik.errors).length > 0) {
        ref.current = formik;
      }
    }, [formik.errors]);

    const reconciliationWarningEntries = useMemo(
      () => Object.entries(reconciliationWarningForModal || {}),
      [reconciliationWarningForModal],
    );

    const visibleSublocations = useMemo(() => {
      if (!showOnlyAssetsWithErrors || !isReconciliation) {
        return sublocations || [];
      }

      const exchangeRate = locationCurrency?.exchangeRate || DEFAULT_EXCHANGE_RATE;

      return (sublocations || [])
        .map((sublocation) => {
          const sublocationId = sublocation?.id;
          const filteredAssets = (sublocation?.assets || []).filter((asset) => {
            const assetId = asset?.id;
            const hasReconciliationFieldVarianceWarnings = getAssetHasReconciliationVarianceWarning({
              asset,
              values: formik.values,
              exchangeRate,
              sublocationId,
            });

            const hasReconciliationVarianceWarnings = reconciliationWarningEntries.some(
              ([fieldKey, hasWarning]) =>
                Boolean(hasWarning) &&
                assetFieldBelongsToIds(fieldKey, sublocationId, assetId) &&
                isVarianceWarningField(fieldKey),
            );

            return Boolean(hasReconciliationFieldVarianceWarnings || hasReconciliationVarianceWarnings);
          });

          return {
            ...sublocation,
            assets: filteredAssets,
          };
        })
        .filter((sublocation) => Array.isArray(sublocation?.assets) && sublocation.assets.length > 0);
    }, [
      showOnlyAssetsWithErrors,
      isReconciliation,
      sublocations,
      locationCurrency?.exchangeRate,
      formik.values,
      reconciliationWarningEntries,
    ]);

    // Progressive rendering: render sublocations in chunks
    const CHUNK_SIZE = 5;
    const [renderedCount, setRenderedCount] = useState(CHUNK_SIZE);

    useEffect(() => {
      setRenderedCount(CHUNK_SIZE);
    }, [visibleSublocations.length]);

    useEffect(() => {
      if (renderedCount < visibleSublocations.length) {
        const timer = setTimeout(() => {
          setRenderedCount((prev) => Math.min(prev + CHUNK_SIZE, visibleSublocations.length));
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [renderedCount, visibleSublocations.length]);

    const chunkedSublocations = useMemo(
      () => visibleSublocations.slice(0, renderedCount),
      [visibleSublocations, renderedCount],
    );

    const visibleSublocationWarnings = useMemo(
      () =>
        Object.fromEntries(
          (visibleSublocations || []).map((sublocation) => [
            sublocation.id,
            Boolean(sublocationWarnings[sublocation.id]),
          ]),
        ),
      [visibleSublocations, sublocationWarnings],
    );

    const visibleAssetWarnings = useMemo(
      () =>
        Object.fromEntries(
          (visibleSublocations || []).map((sublocation) => [
            sublocation.id,
            assetWarningsBySublocation[sublocation.id] || {},
          ]),
        ),
      [visibleSublocations, assetWarningsBySublocation],
    );

    if (visibleSublocations.length === 0) {
      return (
        <NoContent title="No Asset Found" desc={<>No results found, please try changing the search criteria.</>} />
      );
    }

    if (visibleSublocations && visibleSublocations.length > 0) {
      return (
        <FormikProvider value={formik} validateOnChange={false} validateOnBlur={true}>
          {chunkedSublocations?.map((sublocation) => (
            <SublocationMachinesForm
              key={sublocation?.id}
              sublocation={sublocation}
              originalSublocation={allSublocations.find((el) => el.id === sublocation?.id)}
              isReconciliation={isReconciliation}
              isVerified={isVerified}
              searchState={searchState}
              readFromDraft={readFromDraft}
              transactionId={transactionId}
              turnOffDraft={turnOffDraft}
              sortState={sortState}
              onDelete={handleDelete}
              handleSublocationDeleteClick={(id) => handleSublocationDeleteClick(id)}
              saveAsDraftHandler={saveAsDraftHandler}
              onWarningChange={handleSublocationWarningChange}
              onSublocationAssetsUpdate={onSublocationAssetsUpdate}
            />
          ))}
          <SublocationNavigator
            sublocations={visibleSublocations}
            sublocationWarnings={visibleSublocationWarnings}
            assetWarnings={visibleAssetWarnings}
          />
        </FormikProvider>
      );
    }
  },
);

ReconciliationCollectionForm.displayName = displayName;
export default ReconciliationCollectionForm;
